import type { UnitGroup, UnitFeature, UnitCategory } from "@/types";

/**
 * Derive the unique list of features to offer as filter options.
 *
 * Only features belonging to an available unit group (`available > 0`) are
 * included: a feature that exists solely on sold-out units should not appear
 * as a filterable option. Dedupes by feature id. (WEB-1518 / se-vapor PR #180)
 */
export const getAvailableFeatures = (
    unitGroups: UnitGroup[] | undefined,
): UnitFeature[] =>
    Array.from(
        new Map(
            unitGroups
                ?.filter((unitGroup) => unitGroup.available > 0)
                .flatMap((unitGroup) => unitGroup.representative_unit.features)
                .map((feature) => [feature.id, feature]) ?? [],
        ).values(),
    );

/**
 * Derive the unique list of categories to offer as filter options.
 *
 * Only categories belonging to an available unit group (`available > 0`, with
 * a non-null category) are included. Dedupes by category id.
 * (WEB-1518 / se-vapor PR #180)
 */
export const getAvailableCategories = (
    unitGroups: UnitGroup[] | undefined,
): UnitCategory[] =>
    Array.from(
        new Map(
            unitGroups
                ?.filter(
                    (unitGroup) =>
                        unitGroup.representative_unit.category !== null &&
                        unitGroup.available > 0,
                )
                .map((unitGroup) => [
                    unitGroup.representative_unit.category.id,
                    unitGroup.representative_unit.category,
                ]) ?? [],
        ).values(),
    );
